import { ChevronLeft, ChevronRight, Image as ImageIcon, X, ZoomIn } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface Photo {
  url: string;
  serviceName: string;
  instanceName: string;
  fieldName: string;
  fileName?: string;
}

interface PhotoGalleryProps {
  photos: Photo[];
  className?: string;
}

export function PhotoGallery({ photos, className }: PhotoGalleryProps) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const openLightbox = (index: number) => {
    setSelectedPhotoIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedPhotoIndex(null);
  };

  const nextPhoto = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex < photos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };

  const previousPhoto = () => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") {
      nextPhoto();
    } else if (e.key === "ArrowLeft") {
      previousPhoto();
    } else if (e.key === "Escape") {
      closeLightbox();
    }
  };

  // Group photos by service instance
  const groupedPhotos = photos.reduce(
    (acc, photo) => {
      const key = `${photo.serviceName} - ${photo.instanceName}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(photo);
      return acc;
    },
    {} as Record<string, Photo[]>
  );

  if (photos.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Photo Gallery
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <ImageIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">No photos uploaded yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Photos will appear here once they are uploaded during quote creation
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentPhoto = selectedPhotoIndex !== null ? photos[selectedPhotoIndex] : null;

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Photo Gallery
            <Badge variant="secondary" className="ml-auto">
              {photos.length} {photos.length === 1 ? "photo" : "photos"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(groupedPhotos).map(([groupName, groupPhotos]) => (
            <div key={groupName} className="space-y-3">
              <h4 className="font-semibold text-sm text-muted-foreground">{groupName}</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {groupPhotos.map((photo, groupIndex) => {
                  const photoIndex = photos.findIndex((p) => p === photo);
                  return (
                    <button
                      key={photoIndex}
                      type="button"
                      onClick={() => openLightbox(photoIndex)}
                      className={cn(
                        "relative aspect-square overflow-hidden rounded-lg border-2 border-muted",
                        "hover:border-primary hover:scale-105 transition-all duration-200",
                        "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                        "group cursor-pointer"
                      )}
                    >
                      <img
                        src={photo.url}
                        alt={`${photo.fieldName} - Photo ${groupIndex + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center">
                        <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="text-xs text-white truncate">{photo.fieldName}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Lightbox Modal */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent
          className="max-w-7xl w-[95vw] h-[95vh] p-0 overflow-hidden bg-black/95 border-none"
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
        >
          {currentPhoto && (
            <div className="relative w-full h-full flex flex-col">
              {/* Header */}
              <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
                <div className="flex items-center justify-between text-white">
                  <div className="flex-1">
                    <h3 className="font-semibold">{currentPhoto.serviceName}</h3>
                    <p className="text-sm text-gray-300">{currentPhoto.instanceName}</p>
                    <p className="text-xs text-gray-400 mt-1">{currentPhoto.fieldName}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                      {selectedPhotoIndex + 1} / {photos.length}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closeLightbox}
                      className="text-white hover:bg-white/20"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Image Container */}
              <div className="flex-1 flex items-center justify-center p-4 pt-24 pb-20">
                <img
                  src={currentPhoto.url}
                  alt={`${currentPhoto.fieldName}`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {/* Navigation Controls */}
              <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-4">
                <div className="flex items-center justify-center gap-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={previousPhoto}
                    disabled={selectedPhotoIndex === 0}
                    className="text-white hover:bg-white/20 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </Button>
                  <div className="flex gap-2">
                    {photos.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setSelectedPhotoIndex(index)}
                        className={cn(
                          "w-2 h-2 rounded-full transition-all",
                          index === selectedPhotoIndex
                            ? "bg-white w-6"
                            : "bg-white/40 hover:bg-white/60"
                        )}
                        aria-label={`Go to photo ${index + 1}`}
                      />
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={nextPhoto}
                    disabled={selectedPhotoIndex === photos.length - 1}
                    className="text-white hover:bg-white/20 disabled:opacity-30"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
